// ---------------------------------------------------------------------------
// CustomPropertiesStep — Property questions to narrow down template candidates
// ---------------------------------------------------------------------------
// Each option is a property question. option.name = question text.
// option.metadata.choices = answer list. Single-select radio per question.
// Submit: { properties: { [gtp_code]: selected_choice_id } }
// Single matching template (auto_select, no questions) → confirm card that
// submits { template_id }. Not auto-submitted, so Back from the next step still works.
// ---------------------------------------------------------------------------

import { memo, useCallback, useMemo, useState } from 'react';
import { StepHeading } from '@/components/shared/StepHeading';
import { FooterActions } from '@/components/shell/FooterActions';
import { t } from '@/i18n';
import { getStepIcon } from '@/utils/stepIcons';
import type { StepProps } from '@/types/steps';
import styles from './CustomPropertiesStep.module.css';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Choice {
  id: string;
  label: string;
  condition_value: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Strip "prop_" prefix to get the gtp_prezio_code. */
function extractGtpCode(optionId: string): string {
  return optionId.startsWith('prop_') ? optionId.slice(5) : optionId;
}

function getChoices(metadata: Record<string, unknown>): Choice[] {
  const raw = metadata['choices'];
  if (!Array.isArray(raw)) return [];
  return raw as Choice[];
}

// ---------------------------------------------------------------------------
// CustomPropertiesStep
// ---------------------------------------------------------------------------

export const CustomPropertiesStep = memo(function CustomPropertiesStep({
  stepId,
  options,
  onSubmit,
  isSubmitting,
}: StepProps) {
  const questions = options.options;
  const resolvedTemplate = questions.some((q) => q.id.startsWith('prop_'))
    ? null
    : options.auto_select;

  const [selected, setSelected] = useState<Record<string, string>>({});

  const allAnswered = useMemo(
    () => questions.length > 0 && questions.every((q) => selected[q.id] !== undefined),
    [questions, selected],
  );

  const handleChoiceSelect = useCallback((questionId: string, choiceId: string) => {
    setSelected((prev) => ({ ...prev, [questionId]: choiceId }));
  }, []);

  const handleSubmit = useCallback(() => {
    if (!allAnswered) return;
    const properties: Record<string, string> = {};
    for (const q of questions) {
      const choiceId = selected[q.id];
      if (choiceId !== undefined) {
        properties[extractGtpCode(q.id)] = choiceId;
      }
    }
    onSubmit({ properties });
  }, [allAnswered, questions, selected, onSubmit]);

  if (resolvedTemplate) {
    return (
      <>
        <StepHeading
          title={t('steps.retailer_custom_properties.title')}
          icon={getStepIcon(stepId)}
        />
        <div className={styles['groups']}>
          <span className={`${styles['card']} ${styles['selected']}`}>
            <span className={styles['name']}>{resolvedTemplate.name}</span>
          </span>
        </div>
        <FooterActions
          primaryLabel={t('common.next')}
          onPrimary={() => onSubmit({ template_id: resolvedTemplate.id })}
          isLoading={isSubmitting}
        />
      </>
    );
  }

  return (
    <>
      <StepHeading
        title={t('steps.retailer_custom_properties.title')}
        subtitle={t('steps.retailer_custom_properties.subtitle')}
        icon={getStepIcon(stepId)}
      />

      <div className={styles['groups']}>
        {questions.map((q) => {
          const meta = q.metadata;
          const choices = getChoices(meta);

          return (
            <fieldset key={q.id} className={styles['group']} disabled={isSubmitting}>
              <legend className={styles['question']}>{q.name}</legend>
              <div className={styles['options']} role="radiogroup" aria-label={q.name}>
                {choices.map((choice) => {
                  const isSelected = selected[q.id] === choice.id;
                  return (
                    <label key={choice.id} className={styles['option']}>
                      <input
                        type="radio"
                        className={styles['radio']}
                        name={`cprop-${q.id}`}
                        value={choice.id}
                        checked={isSelected}
                        onChange={() => handleChoiceSelect(q.id, choice.id)}
                      />
                      <span className={`${styles['card']} ${isSelected ? styles['selected'] : ''}`}>
                        <span className={styles['name']}>{choice.label}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </fieldset>
          );
        })}
      </div>

      <FooterActions
        primaryLabel={t('common.next')}
        onPrimary={handleSubmit}
        isLoading={isSubmitting}
        disabled={!allAnswered}
      />
    </>
  );
});
